import BASE_URL from '../../config/env';
import {apiClient} from '../apiClient';
import {AxiosError} from 'axios';
import {
  parseApiErrorWithLocalization,
  BackendErrorData,
} from '../../utils/custom-errors/parseApiError';
import {
  IAdvancedSearchTabsDataResponse,
  IOperatorData,
  ISearchUser,
  ISearchValidationResponse,
  SearchRequest,
  SearchResultRequest,
  IRunConceptualSearchResponse,
  IConceptualSearchStatusResponse,
  ISaveRequest,
  ISaveResponse,
  ISearchStatusResponse,
  SessionSearchItem,
  ISearchUniqueDocIds,
  ISearchUniqueCountRequest,
  ISearchUniqueCountResponse,
  GetNestedSearchesParams,
  GetNestedSearchesResponse,
  AddSearchMessageToQueueRequest,
  SearchResponse,
  IValidateCriteriaRequest,
} from '../types/sessionSearch.type';
import {
  formatNestedSearchContextId,
  NESTED_SEARCH_CONTEXT_TYPE,
} from '../../components/session-search/utilities/nestedSearchUtils';

const searchEndpoint = (path: string) =>
  `${BASE_URL}/searching/api/v1/search${path}`;

/** SEARCHGROUP ids may be -1 (My Searches); must not pass through > 0 filter */
const toNestedSearchContextIdParam = (
  params: GetNestedSearchesParams
): string => {
  if (params.contextType === NESTED_SEARCH_CONTEXT_TYPE.SEARCHGROUP) {
    return String(params.contextId ?? '').trim();
  }
  return formatNestedSearchContextId(params.contextId);
};

export const getAdvancedSearchTabsData = async (
  buttonType: string,
  indexID?: number
): Promise<IAdvancedSearchTabsDataResponse> => {
  const indexIDParam =
    indexID === undefined ? '' : `&indexID=${encodeURIComponent(indexID)}`;
  const {data} = await apiClient.get(
    searchEndpoint(
      `/tabs/data?buttonType=${encodeURIComponent(buttonType)}${indexIDParam}`
    )
  );
  return data;
};

export const getOperatorData = async (
  datatype: string
): Promise<IOperatorData[]> => {
  const {data} = await apiClient.get(
    searchEndpoint(
      `/operators/by-type?datatype=${encodeURIComponent(datatype)}`
    )
  );
  return data;
};

export const getAutocomplete = async (
  text: string,
  type: 'TEXT' | 'COMMENTS'
): Promise<string[]> => {
  if (!text) {
    return [];
  }
  if (text.length < 3) {
    return [];
  }

  let data: string[] = [];
  if (type === 'TEXT') {
    data = await getAutocompleteText(text);
  } else if (type === 'COMMENTS') {
    data = await getAutocompleteComments(text);
  }
  return data;
};

const getAutocompleteText = async (text: string): Promise<string[]> => {
  const {data} = await apiClient.get(
    searchEndpoint(`/autocomplete/text?search=${encodeURIComponent(text)}`)
  );
  return data;
};

const getAutocompleteComments = async (text: string): Promise<string[]> => {
  const {data} = await apiClient.get(
    searchEndpoint(
      `/autocomplete/comments-remarks?search=${encodeURIComponent(text)}`
    )
  );
  return data;
};

export const getUserList = async (): Promise<ISearchUser[]> => {
  const {data} = await apiClient.get(searchEndpoint(`/users`));
  return data;
};

export const getSearchUniqueDocIds = async (
  searchId: number,
  redirectToSearch: string,
  searchName: string
): Promise<ISearchUniqueDocIds> => {
  const {data} = await apiClient.get<ISearchUniqueDocIds>(
    searchEndpoint(`/view/searches`),
    {
      params: {
        searchId,
        redirectToSearch,
        searchName,
      },
    }
  );
  return data;
};

export const getSearchUniqueCount = async (
  payload: ISearchUniqueCountRequest[]
): Promise<ISearchUniqueCountResponse> => {
  const {data} = await apiClient.post<ISearchUniqueCountResponse>(
    searchEndpoint('/unique-count'),
    payload
  );
  return data;
};

export const getNestedSearches = async (
  params: GetNestedSearchesParams
): Promise<GetNestedSearchesResponse> => {
  const contextId = toNestedSearchContextIdParam(params);

  try {
    const {data} = await apiClient.post<GetNestedSearchesResponse>(
      searchEndpoint('/nested-searches'),
      undefined,
      {
        params: {
          contextId,
          contextType: params.contextType,
          searchTypeCode: params.searchTypeCode,
        },
      }
    );

    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'Search'
    );
  }
};

export const validateAnalyticCriteria = async (
  searchCriteria: string
): Promise<ISearchValidationResponse> => {
  const {data} = await apiClient.get(
    searchEndpoint(
      `/validate/analyticscriteria?searchCriteria=${encodeURIComponent(searchCriteria)}`
    )
  );
  return data;
};

export const validateWorkProductCriteria = async (
  searchCriteria: string
): Promise<ISearchValidationResponse> => {
  const {data} = await apiClient.post(
    searchEndpoint('/validate/workproductcriteria'),
    searchCriteria,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return data;
};

export const validateContentmetaData = async (
  payload: IValidateCriteriaRequest
) => {
  const {data} = await apiClient.post(
    searchEndpoint(`/validate/contentetadata`),
    payload,
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return data;
};

export const validateSearchCriteria = async (
  payload: IValidateCriteriaRequest
) => {
  const {data} = await apiClient.post(
    searchEndpoint(`/validate/searchcriteria`),
    payload,
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return data;
};

export const runSearch = async (
  payload: SearchRequest
): Promise<GetNestedSearchesResponse> => {
  const {data} = await apiClient.post<GetNestedSearchesResponse>(
    searchEndpoint(`/runsearch`),
    payload
  );
  return data;
};

export const addSearchToQueue = async (
  payload: AddSearchMessageToQueueRequest
): Promise<SearchResponse> => {
  const {data} = await apiClient.post<SearchResponse>(
    searchEndpoint(`/add-to-search-queue`),
    payload
  );
  return data;
};

export const reloadSearchResult = async (payload: SearchResultRequest) => {
  const {data} = await apiClient.post(
    searchEndpoint(`/reload/result`),
    payload
  );
  return data;
};

export const getConceptualSearchStatus = async (
  searchId: string | number
): Promise<IConceptualSearchStatusResponse> => {
  // Set other property defaults
  const payload = {
    searchId: searchId,
    resultType: 'ADVANCEDSEARCH',
    isElasticSearch: 0,
    isSavedSearch: false,
  };

  const {data} = await apiClient.post(
    searchEndpoint('/reload/conceptual-result'),
    payload
  );
  return data;
};

export const runConceptualSearch = async (
  searchId: string | number
): Promise<IRunConceptualSearchResponse> => {
  const {data} = await apiClient.get(
    searchEndpoint(
      `/startconceptualsearch?searchId=${encodeURIComponent(searchId)}`
    )
  );
  return data;
};

export const saveSearch = async (
  payload: ISaveRequest
): Promise<ISaveResponse> => {
  const {data} = await apiClient.post(
    searchEndpoint(`/view/saved-search-result`),
    payload
  );
  return data;
};

export const getSearchStatus = async (
  searchId: number
): Promise<ISearchStatusResponse> => {
  const {data} = await apiClient.get(
    searchEndpoint(`/status?searchId=${searchId}`)
  );
  return data;
};

export const notification = async (
  searchId: number,
  notificationType: string,
  searchNotifyType: string,
  sessionSearch: SessionSearchItem
): Promise<number> => {
  const {data} = await apiClient.post(searchEndpoint(`/add/notification`), {
    searchId,
    notificationType,
    searchNotifyType,
    sessionSearch: [sessionSearch],
  });
  return data;
};
